import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { publicAPI } from '../services/api';
import CertificateTemplate from '../components/CertificateTemplate';

const BASE_URL = window.location.origin;

export default function CertificatePage() {
  const { hash } = useParams();
  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [manualHash, setManualHash] = useState('');
  const [showQR, setShowQR]         = useState(false);
  const [showCert, setShowCert]     = useState(false);

  const verify = async (h) => {
    if (!h?.trim()) return;
    setLoading(true); setResult(null);
    try {
      const res = await publicAPI.verifyCertificate(h.trim());
      setResult(res.data);
    } catch (err) {
      setResult({ valid: false, error: err.response?.data?.error || 'Certificate not found' });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (hash) verify(hash);
    else setLoading(false);
  }, [hash]);

  const cert    = result?.certificate || result?.cert;
  const certUrl = cert ? `${BASE_URL}/verify/${cert.cert_hash}` : null;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-0)' }}>

      {/* Hero */}
      <section style={{ paddingTop: 'calc(var(--nav-h) + clamp(60px,8vw,100px))', paddingBottom: 'clamp(60px,8vw,100px)', background: 'linear-gradient(180deg, var(--bg-1) 0%, var(--bg-0) 100%)' }}>
        <div className="container" style={{ maxWidth: 640 }}>
          <div className="section-label label" style={{ marginBottom: 20 }}>Certificate Verification</div>
          <h1 className="display-lg" style={{ marginBottom: 16 }}>
            Verify a<br /><span style={{ color: 'var(--gold)' }}>MilleHills Certificate</span>
          </h1>
          <p className="body-lg" style={{ color: 'var(--t-secondary)', maxWidth: 480 }}>
            Enter a certificate hash or scan the QR code to verify authenticity and download a copy.
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: 'clamp(40px,6vw,80px) 0' }}>
        <div className="container" style={{ maxWidth: 640 }}>

          {/* Search bar */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 48 }}>
            <input value={manualHash} onChange={e => setManualHash(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') verify(manualHash); }}
              placeholder="Paste certificate hash here…"
              style={{ flex: 1, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', color: 'var(--t-primary)', padding: '12px 16px', fontSize: '0.875rem', fontFamily: 'monospace' }}
            />
            <button onClick={() => verify(manualHash)} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>Verify →</button>
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--t-muted)' }}>
              <div style={{ width: 32, height: 32, border: '2px solid rgba(255,255,255,0.08)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 16px' }} />
              Verifying certificate…
            </div>
          )}

          {/* Empty state */}
          {!loading && !result && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--t-muted)' }}>
              <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.2 }}>◈</div>
              <p style={{ fontSize: '0.9rem', marginBottom: 8 }}>Enter a certificate hash above to verify it.</p>
              <p style={{ fontSize: '0.82rem', opacity: 0.6 }}>Or scan the QR code on a MilleHills certificate.</p>
            </div>
          )}

          {/* Result */}
          {!loading && result && (
            <div style={{ border: `2px solid ${result.valid ? 'rgba(62,207,142,0.4)' : 'rgba(240,96,96,0.4)'}`, borderRadius: 'var(--r-xl)', overflow: 'hidden', animation: 'scaleIn 0.3s ease' }}>

              {/* Status banner */}
              <div style={{ padding: '20px 28px', background: result.valid ? 'rgba(62,207,142,0.08)' : 'rgba(240,96,96,0.08)', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0, background: result.valid ? 'rgba(62,207,142,0.15)' : 'rgba(240,96,96,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: result.valid ? '#3ecf8e' : '#f06060' }}>
                  {result.valid ? '✓' : '✕'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.1rem', color: result.valid ? '#3ecf8e' : '#f06060' }}>
                    {result.valid ? 'Certificate Verified' : 'Invalid Certificate'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--t-muted)', marginTop: 2 }}>
                    {result.valid ? 'This is an authentic MilleHills certificate.' : result.error || 'This certificate could not be verified.'}
                  </div>
                </div>
                {result.valid && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => setShowQR(v => !v)} style={{ padding: '8px 12px', borderRadius: 8, background: showQR ? 'rgba(232,164,58,0.15)' : 'var(--bg-2)', border: `1px solid ${showQR ? 'rgba(232,164,58,0.4)' : 'var(--border)'}`, color: showQR ? 'var(--gold)' : 'var(--t-muted)', fontSize: '0.78rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                      QR
                    </button>
                    <button onClick={() => setShowCert(true)} className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '8px 14px' }}>
                      ⬇ Download
                    </button>
                  </div>
                )}
              </div>

              {/* QR panel */}
              {showQR && certUrl && (
                <div style={{ padding: '20px 28px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <div style={{ background: '#fff', padding: 10, borderRadius: 10, flexShrink: 0 }}>
                    <QRCodeSVG value={certUrl} size={100} level="H" includeMargin={false} />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, marginBottom: 6 }}>Scan to Verify</div>
                    <p style={{ fontSize: '0.82rem', color: 'var(--t-secondary)', lineHeight: 1.6, marginBottom: 10 }}>Share this QR code with employers or institutions to instantly verify authenticity.</p>
                    <button onClick={() => { navigator.clipboard.writeText(certUrl); alert('Link copied!'); }} style={{ fontSize: '0.78rem', color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      Copy verification link →
                    </button>
                  </div>
                </div>
              )}

              {/* Certificate details */}
              {cert && (
                <div style={{ padding: '28px', background: 'var(--bg-1)', display: 'flex', flexDirection: 'column', gap: 20 }}>

                  {/* Recipient card */}
                  <div style={{ textAlign: 'center', padding: '24px', background: 'var(--bg-2)', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, background: 'radial-gradient(circle, rgba(232,164,58,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
                    <div style={{ fontSize: '0.7rem', color: 'var(--t-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>This certifies that</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', fontWeight: 700, color: 'var(--gold)', marginBottom: 8 }}>{cert.recipient_name}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--t-secondary)' }}>{cert.recipient_email}</div>
                    <div style={{ width: 60, height: 2, background: 'var(--gold)', margin: '16px auto', opacity: 0.4 }} />
                    <div style={{ fontSize: '0.8rem', color: 'var(--t-muted)', marginBottom: 4 }}>has successfully completed</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 600 }}>{cert.program_title}</div>
                    {cert.cohort_name && <div style={{ fontSize: '0.82rem', color: 'var(--t-muted)', marginTop: 6 }}>Cohort: {cert.cohort_name}</div>}
                  </div>

                  {/* Metadata */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      ['Issued',         cert.issued_at  ? new Date(cert.issued_at).toLocaleDateString('en-RW',  { year: 'numeric', month: 'long', day: 'numeric' }) : '—'],
                      ['Expires',        cert.expires_at ? new Date(cert.expires_at).toLocaleDateString('en-RW', { year: 'numeric', month: 'long', day: 'numeric' }) : 'No expiry'],
                      ['Certificate ID', cert.cert_hash?.slice(0, 16) + '…'],
                      ['Status',         cert.is_revoked ? 'Revoked' : 'Valid'],
                    ].map(([k, v]) => (
                      <div key={k} style={{ padding: '12px 16px', background: 'var(--bg-2)', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.65rem', color: 'var(--t-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{k}</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 500, fontFamily: k === 'Certificate ID' ? 'monospace' : 'inherit', color: k === 'Status' && cert.is_revoked ? 'var(--red)' : 'var(--t-primary)' }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Full hash */}
                  <div style={{ padding: '12px 16px', background: 'var(--bg-2)', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--t-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Full Hash</div>
                    <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'var(--t-secondary)', wordBreak: 'break-all' }}>{cert.cert_hash}</div>
                  </div>

                  {/* Download CTA */}
                  <button onClick={() => setShowCert(true)} className="btn btn-primary" style={{ justifyContent: 'center', fontSize: '0.95rem' }}>
                    ⬇ View &amp; Download Certificate
                  </button>

                  <div style={{ textAlign: 'center' }}>
                    <Link to="/programs" style={{ fontSize: '0.85rem', color: 'var(--gold)' }}>Explore our Programs →</Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Certificate preview modal */}
      {showCert && cert && (
        <CertificateTemplate cert={cert} onClose={() => setShowCert(false)} />
      )}
    </div>
  );
}